/**
 * PO putaway audit — same-brand tubs among a loaded purchase order's STORED
 * locations (2026-09-01).
 *
 * The single brand-check guard only fires on a location CHANGE. Items on a PO
 * arrive with their locations already saved, so two same-brand items quietly
 * headed for one tub raise no prompt at all. When a PO is loaded in the grid,
 * every { sku, location } pair goes to POST /api/warehouse/locations/
 * brand-check-batch in one call, and these helpers turn the answer into the
 * banner above the grid and the amber chip on each affected card.
 *
 * Advisory by design: a failed audit shows nothing rather than blocking.
 */

/** The PO SKUs currently on the grid with a usable saved location. */
export function collectPoAuditItems(products, auditSkus) {
  const skuSet = new Set((auditSkus || []).map((sku) => String(sku || '').trim().toUpperCase()));
  if (skuSet.size === 0) return [];
  const items = [];
  const seen = new Set();
  (products || []).forEach((product) => {
    const sku = String(product?.sku || '').trim();
    if (!sku || !skuSet.has(sku.toUpperCase())) return;
    const location = String(product?.binLocation || '').trim();
    if (!location || /^not set$/i.test(location)) return;
    const pairKey = `${sku.toUpperCase()}|${location.toUpperCase()}`;
    if (seen.has(pairKey)) return;
    seen.add(pairKey);
    items.push({ sku, location });
  });
  return items;
}

/**
 * Flatten the batch endpoint's per-slot findings into per-SKU flags for the
 * cards plus one summary line per finding for the banner.
 */
export function mapTubClashSlots(slots) {
  const bySku = {};
  const lines = [];

  const flagFor = (sku, slot, vendor) => {
    const key = String(sku || '').trim().toUpperCase();
    if (!bySku[key]) bySku[key] = { slotKey: slot.slotKey, vendor, partners: [], existing: [] };
    return bySku[key];
  };

  (slots || []).forEach((slot) => {
    (slot.batchClashes || []).forEach((group) => {
      const skus = group.skus || [];
      lines.push(`${slot.slotKey} · ${group.vendor} — ${skus.join(' + ')} arrive together`);
      skus.forEach((sku) => {
        const flag = flagFor(sku, slot, group.vendor);
        skus.forEach((partner) => {
          if (String(partner).toUpperCase() === String(sku).toUpperCase()) return;
          if (flag.partners.includes(partner)) return;
          flag.partners.push(partner);
        });
      });
    });

    (slot.existingClashes || []).forEach((clash) => {
      const existing = clash.existing || [];
      const existingSkus = existing.map((product) => product.sku).filter(Boolean);
      lines.push(
        `${slot.slotKey} · ${clash.sku} joins ${clash.vendor} stock already there (${existingSkus.join(', ')})`
      );
      const flag = flagFor(clash.sku, slot, clash.vendor);
      existing.forEach((product) => {
        if (flag.existing.some((entry) => entry.sku === product.sku)) return;
        flag.existing.push(product);
      });
    });
  });

  return { bySku, lines };
}

/** Compact label for the card's location chip; the tooltip carries the rest. */
export function tubClashChipLabel(clash) {
  if (!clash) return null;
  if (clash.partners.length > 0) {
    return `Same tub as ${clash.partners.join(' + ')}`;
  }
  const skus = clash.existing.map((product) => product.sku).filter(Boolean);
  return `Tub holds ${clash.vendor}: ${skus.join(', ')}`;
}
